import React, { useState } from 'react'
import { Suggestion } from '../../types/mockup'
import { ImpactAnalysis } from './ImpactAnalysis'
import { NotificationPreview } from './NotificationPreview'
import { CascadeVisualization } from './CascadeVisualization'
import './SuggestionModal.css'

interface SuggestionModalProps {
  suggestion: Suggestion
  isOpen: boolean
  onClose: () => void
  onApply: (suggestionId: string) => void
  onDismiss: (suggestionId: string, reason?: string) => void
  onEditNotifications?: (suggestionId: string, notifications: any[]) => void
}

type ModalTab = 'analysis' | 'notifications' | 'cascade'

export const SuggestionModal: React.FC<SuggestionModalProps> = ({
  suggestion,
  isOpen,
  onClose,
  onApply,
  onDismiss,
  onEditNotifications
}) => {
  const [activeTab, setActiveTab] = useState<ModalTab>('analysis')
  const [showDismissReason, setShowDismissReason] = useState(false)
  const [dismissReason, setDismissReason] = useState('')
  const [isApplying, setIsApplying] = useState(false)

  if (!isOpen) return null

  // Handle apply with loading state
  const handleApply = async () => {
    setIsApplying(true)
    try {
      await onApply(suggestion.id)
      onClose()
    } catch (error) {
      console.error('Failed to apply suggestion:', error)
      // Error handling could be enhanced with toast notifications
    } finally {
      setIsApplying(false)
    }
  }

  // Handle dismiss with reason
  const handleDismiss = () => {
    if (suggestion.riskLevel === 'critical') {
      setShowDismissReason(true)
    } else {
      onDismiss(suggestion.id)
      onClose()
    }
  }

  const handleDismissWithReason = () => {
    onDismiss(suggestion.id, dismissReason || 'User dismissed')
    setShowDismissReason(false)
    setDismissReason('')
    onClose()
  }

  // Risk level styling
  const getRiskLevelClass = (level: string): string => {
    switch (level) {
      case 'critical': return 'modal-risk-critical'
      case 'warning': return 'modal-risk-warning'
      case 'info': return 'modal-risk-info'
      default: return ''
    }
  }

  // Confidence display
  const getConfidenceDisplay = (confidence: number): string => {
    if (confidence >= 0.8) return 'High Confidence'
    if (confidence >= 0.6) return 'Medium Confidence'
    return 'Low Confidence'
  }

  const getConfidenceClass = (confidence: number): string => {
    if (confidence >= 0.8) return 'confidence-high'
    if (confidence >= 0.6) return 'confidence-medium'
    return 'confidence-low'
  }

  return (
    <div className="suggestion-modal-overlay" onClick={onClose}>
      <div 
        className={`suggestion-modal ${getRiskLevelClass(suggestion.riskLevel)}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <h2 className="modal-title">{suggestion.headline}</h2>
            <div className="header-badges">
              <span className={`confidence-badge ${getConfidenceClass(suggestion.confidence)}`}>
                {getConfidenceDisplay(suggestion.confidence)} ({Math.round(suggestion.confidence * 100)}%)
              </span>
              <span className={`risk-badge risk-${suggestion.riskLevel}`}>
                {suggestion.riskLevel.toUpperCase()}
              </span>
            </div>
          </div>
          <button 
            className="modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >
            Impact Analysis
          </button>
          <button
            className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            Notifications ({suggestion.notifications.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'cascade' ? 'active' : ''}`}
            onClick={() => setActiveTab('cascade')}
          >
            Change Process
          </button>
        </div>

        {/* Tab Content */}
        <div className="modal-content">
          {activeTab === 'analysis' && (
            <ImpactAnalysis suggestion={suggestion} />
          )}
          
          {activeTab === 'notifications' && (
            <NotificationPreview 
              suggestion={suggestion}
              onEditNotifications={onEditNotifications}
            />
          )}
          
          {activeTab === 'cascade' && (
            <CascadeVisualization suggestion={suggestion} />
          )}
        </div>

        {/* Actions Footer */}
        <div className="modal-actions">
          <div className="action-info">
            {suggestion.expiresAt && (
              <span className="expiry-warning">
                Expires: {new Date(suggestion.expiresAt).toLocaleString()}
              </span>
            )}
          </div>
          
          <div className="action-buttons">
            <button 
              className="btn-dismiss"
              onClick={handleDismiss}
              disabled={isApplying}
            >
              Dismiss
            </button>
            
            <button 
              className="btn-apply"
              onClick={handleApply}
              disabled={isApplying || suggestion.confidence < 0.6}
            >
              {isApplying ? 'Applying...' : 'Apply Changes'}
            </button>
          </div>
        </div>

        {/* Dismiss Reason Modal */}
        {showDismissReason && (
          <div className="dismiss-reason-overlay">
            <div className="dismiss-reason-modal">
              <h3>Dismiss Critical Suggestion</h3>
              <p>This is a critical suggestion that could significantly impact the event. Please provide a reason for dismissing it:</p>
              
              <div className="reason-options">
                <label className="reason-option">
                  <input 
                    type="radio" 
                    name="reason"
                    value="Already handled manually"
                    onChange={(e) => setDismissReason(e.target.value)}
                  />
                  Already handled manually
                </label>
                <label className="reason-option">
                  <input 
                    type="radio" 
                    name="reason"
                    value="Not applicable to this event"
                    onChange={(e) => setDismissReason(e.target.value)}
                  />
                  Not applicable to this event
                </label>
                <label className="reason-option">
                  <input 
                    type="radio" 
                    name="reason"
                    value="Risk acceptable"
                    onChange={(e) => setDismissReason(e.target.value)}
                  />
                  Risk acceptable
                </label>
                <label className="reason-option">
                  <input 
                    type="radio" 
                    name="reason"
                    value="Other"
                    onChange={(e) => setDismissReason(e.target.value)}
                  />
                  Other (specify below)
                </label>
              </div>
              
              {dismissReason === 'Other' && (
                <textarea
                  className="custom-reason"
                  placeholder="Please specify your reason..."
                  value={dismissReason === 'Other' ? '' : dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                />
              )}
              
              <div className="reason-actions">
                <button 
                  className="btn-cancel"
                  onClick={() => setShowDismissReason(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-confirm-dismiss"
                  onClick={handleDismissWithReason}
                  disabled={!dismissReason}
                >
                  Dismiss Suggestion
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SuggestionModal